import {useStyles} from '@wix/tpa-settings/react'
import {IMAGE_POSITION, WIDGET_TYPE, getEventMainImage, getEventTitle, hasImage} from '@wix/wix-events-commons-statics'
import {widgetDataHooks as DH} from '@wix/wix-events-data-hooks'
import {ImageResizeOptions, WowImage} from '@wix/wix-ui-tpa/cssVars'
import classNames from 'classnames'
import React from 'react'
import {getSeo} from '../../../../../commons/selectors/environment'
import {resolveFocalPoint} from '../../../../../commons/utils/image'
import stylesParams from '../../../stylesParams'
import {useWidgetState} from '../../hooks/state-provider'
import s from './event-image.scss'
import {classes} from './event-image.st.css'
import {EventImageProps} from './interfaces'

export const EventImage = ({
  event,
  position = IMAGE_POSITION.CENTER_CENTER,
  className,
  noBackground,
  backgroundFallback,
  opacityFallback,
  noLQIP,
  calendar,
}: EventImageProps) => {
  const {get} = useStyles()
  const seo = useWidgetState(getSeo)
  const shouldUseLQIP = !noLQIP
  const singleLayout = get(stylesParams.widgetType) === WIDGET_TYPE.SINGLE
  const imagePosition = singleLayout
    ? position
    : calendar
      ? get(stylesParams.calendarImagePosition)
      : get(stylesParams.multiImagePosition)
  const focalPoint = resolveFocalPoint(imagePosition)

  const getOpacityClass = () => {
    if (singleLayout && opacityFallback) {
      return classes.singleOpacity
    } else {
      if (opacityFallback) {
        return calendar ? classes.calendarImageOpacityWithFallback : classes.multiOpacityWithFallback
      } else {
        return calendar ? classes.calendarImageOpacity : classes.multiOpacity
      }
    }
  }

  const getContainVisibilityClass = () => {
    if (calendar) {
      return s.calendarContainVisibility
    } else {
      return singleLayout ? s.singleContainVisibility : s.multiContainVisibility
    }
  }

  const getCoverVisibilityClass = () => {
    if (calendar) {
      return s.calendarCoverVisibility
    } else {
      return singleLayout ? s.singleCoverVisibility : s.multiCoverVisibility
    }
  }

  const getBackgroundClass = () => {
    if (singleLayout) {
      return s.singleBackground
    } else if (backgroundFallback) {
      return calendar ? s.calendarImageBackgroundWithFallback : s.multiBackgroundWithFallback
    } else {
      return calendar ? s.calendarImageBackground : s.multiBackground
    }
  }

  const getImageClasses = () => {
    if (singleLayout) {
      return []
    } else if (calendar) {
      return [classes.calendarImageBorder, s.calendarImageBorderRadius]
    } else {
      return [classes.border, s.borderRadius]
    }
  }

  const props = {
    'data-hook': DH.image,
    src: getEventMainImage(event)?.id,
    alt: getEventTitle(event),
    sourceWidth: getEventMainImage(event)?.width,
    sourceHeight: getEventMainImage(event)?.height,
    focalPoint,
    shouldUseLQIP,
    fluid: true,
    isSEOBot: seo,
  }

  return (
    <>
      {!noBackground ? (
        <div
          className={classNames(s.container, getBackgroundClass(), {
            [s.padding]: !singleLayout && !calendar,
            [s.calendarPadding]: !singleLayout && calendar,
          })}
          data-hook="image-background"
        />
      ) : null}
      {hasImage(event) ? (
        <div
          className={classNames(s.container, className, {
            [s.padding]: !singleLayout && !calendar,
            [s.calendarPadding]: !singleLayout && calendar,
          })}
        >
          <WowImage
            {...props}
            className={classNames(getOpacityClass(), s.minSize, getContainVisibilityClass(), getImageClasses())}
            resize={ImageResizeOptions.contain}
          />
          <WowImage
            {...props}
            className={classNames(getOpacityClass(), s.minSize, getCoverVisibilityClass(), getImageClasses())}
            resize={ImageResizeOptions.cover}
          />
        </div>
      ) : null}
    </>
  )
}
